import { useCallback, useEffect, useRef, useState } from "react";
import { DEMO_TOUR_STEPS, type DemoTourStep } from "./demoTourSteps";
import { LessonMarkdown } from "./LessonMarkdown";

export function StudentIdeTourPanel({ steps }: { steps?: readonly DemoTourStep[] }) {
  const [step, setStep] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  const [copyToast, setCopyToast] = useState<"success" | "error" | null>(null);
  const copyToastTimerRef = useRef<number | null>(null);
  const effective = steps && steps.length > 0 ? steps : DEMO_TOUR_STEPS;
  const current = effective[step]!;
  const last = step >= effective.length - 1;

  const hideCopyToast = useCallback(() => {
    if (copyToastTimerRef.current !== null) {
      window.clearTimeout(copyToastTimerRef.current);
      copyToastTimerRef.current = null;
    }
    setCopyToast(null);
  }, []);

  const showCopyToast = useCallback(
    (kind: "success" | "error") => {
      if (copyToastTimerRef.current !== null) {
        window.clearTimeout(copyToastTimerRef.current);
      }
      setCopyToast(kind);
      copyToastTimerRef.current = window.setTimeout(() => {
        copyToastTimerRef.current = null;
        setCopyToast(null);
      }, 2200);
    },
    [setCopyToast]
  );

  useEffect(() => hideCopyToast, [hideCopyToast]);
  useEffect(() => {
    hideCopyToast();
  }, [hideCopyToast, step]);

  const copyReference = useCallback(async () => {
    const text = current.referenceCode;
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      showCopyToast("success");
    } catch {
      showCopyToast("error");
    }
  }, [current.referenceCode, showCopyToast]);

  return (
    <section className="ide-editor-panel ide-tour-panel" aria-label="Lesson instructions">
      <div className="ide-tour-toolbar">
        <div className="ide-panel-label ide-tour-toolbar-label">Lesson instructions</div>
        <div className="ide-tour-toolbar-meta">
          <span className="ide-tour-step-pill" aria-live="polite">
            Step {step + 1} of {effective.length}
          </span>
          <button
            type="button"
            className="btn secondary ide-btn tight"
            onClick={() => setCollapsed((c) => !c)}
            aria-expanded={!collapsed}
          >
            {collapsed ? "Show" : "Hide"}
          </button>
        </div>
      </div>
      {!collapsed ? (
        <>
          <div className="ide-tour-body">
            <h2 className="ide-tour-step-title">{current.title}</h2>
            <LessonMarkdown lines={current.body} />
            {current.referenceCode ? (
              <div className="ide-reference-block">
                <div className="ide-reference-head">
                  <span className="ide-reference-label">Reference snippet (read-only)</span>
                  <button type="button" className="btn secondary ide-btn tight" onClick={() => void copyReference()}>
                    Copy
                  </button>
                </div>
                <pre className="ide-reference-pre">{current.referenceCode}</pre>
                {copyToast ? (
                  <div
                    className={`ide-copy-toast ide-copy-toast--${copyToast}`}
                    role="status"
                    aria-live="polite"
                    aria-atomic="true"
                  >
                    {copyToast === "success" ? "Copied to clipboard" : "Couldn't copy - select manually"}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="ide-tour-nav">
            <button
              type="button"
              className="btn secondary ide-btn"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
            >
              Previous
            </button>
            <button
              type="button"
              className="btn primary ide-btn"
              onClick={() => setStep((s) => Math.min(effective.length - 1, s + 1))}
              disabled={last}
            >
              Next
            </button>
          </div>
        </>
      ) : null}
    </section>
  );
}
